/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useId, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { type EREProvider } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ALL_DURATIONS,
  DEFAULT_DURATION_SELECTION,
  durationOptions,
  durationSelectionLabel,
  isDurationSelected,
  toggleDurationSelection,
} from '../lib/durationOptions';
import { formatInteger } from '../lib/formatters';
import { fillTemplate } from '../lib/templateVars.js';
import { SURFACES, trackEvent } from '../lib/gtag';
import { ArrowDown, Calculator, Gauge, Zap } from 'lucide-react';
import { CustomDropdown } from './CustomDropdown';
import { LazyOnOpen } from './LazyOnOpen';
import {
  EstimateKwhModal,
  MidCheckerModal,
  loadEstimateKwhModal,
  loadMidCheckerModal,
  preloadOnIntent,
} from './lazyModals';
import { VOLUME_MANUAL_MAX, VOLUME_MAX, VOLUME_MIN, VOLUME_STEP } from '../lib/kwhEstimate';
import { defaultAnnualKwh } from '../lib/earnings';
import { scrollToCompare } from '../lib/compareAnchor';
import { cn } from '../lib/cn';
import { rememberCompareInputs } from '../lib/compareMemory';
import { compareInputsFromUrl, hasCompareFilterParam } from '../lib/compareInputsFromUrl';
import { ResumeCompareChip } from './comparison/ResumeCompareChip';

interface FiltersProps {
  /** The rows the comparer will filter — the source of the duration list. Empty
   *  while /api/providers is still in flight, which the helper treats as "not
   *  known yet" and answers with the full canonical list. */
  providers?: EREProvider[];
}

export const Filters: React.FC<FiltersProps> = ({ providers }) => {
  const { t, language } = useLanguage();
  // The visible "Contractduur" names the select (CustomDropdown's labelledBy).
  const durationLabelId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const audience = searchParams.get('audience') || 'Particulier';
  // Same audience-aware fallback as the results (src/lib/earnings.ts): a
  // household starts at 3 000 kWh (since 2026-10-30), a zakelijke bezoeker at
  // the business default of 6 000 kWh.
  const defaultVolume = defaultAnnualKwh(audience);
  const volume = parseInt(searchParams.get('volume') || String(defaultVolume)) || defaultVolume;
  // '1 jaar' when the address names none (since 2026-10-30; every looptijd
  // from 2026-10-16), as the results read it (src/lib/comparerParams.ts).
  const contractDuration = searchParams.get('contractDuration') || DEFAULT_DURATION_SELECTION;
  const [volumeInput, setVolumeInput] = useState(formatInteger(volume, language));
  const [estimatorOpen, setEstimatorOpen] = useState(false);

  // `?estimator=<surface>` opens the kWh estimator from elsewhere on the page
  // (the results header) without that surface owning a second modal. The
  // param is consumed — stripped from the URL — so a reload or a shared link
  // does not reopen it.
  const estimatorRequest = searchParams.get('estimator');
  useEffect(() => {
    if (!estimatorRequest) return;
    // Only the surface that sets it; anything else (a mangled link, a
    // crawler's guess at "=1", the hero's `=hero` link retired on 2026-10-21)
    // is stripped without opening, so the funnel never records a phantom entry.
    const surface = estimatorRequest === 'results' ? SURFACES.resultsBar : null;
    if (surface) {
      setEstimatorOpen(true);
      trackEvent('estimator_open', { surface, language });
    }
    // Not setSearchParams: that navigates to "?…" alone and drops the
    // #vergelijken / #compare hash the address may carry, so a reload or a
    // copied link would no longer land on the comparer.
    const next = new URLSearchParams(searchParams);
    next.delete('estimator');
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatorRequest]);
  const [midCheckerOpen, setMidCheckerOpen] = useState(false);

  // Keep input in sync if URL volume changes from another source (e.g. estimator modal).
  useEffect(() => {
    setVolumeInput(formatInteger(volume, language));
  }, [volume, language]);

  // Remember the comparison this address carries, so a later visit can offer
  // it again through the chip below (src/lib/compareMemory.ts decides what is
  // kept and when; plan section 12.4 B2). The card writes it rather than the
  // results engine: the address is all it needs, read the comparer's way
  // (compareInputsFromUrl.ts) — the audience whitelisted, unlike this card's
  // own `audience` above. One write per change of one of the five values; an
  // address without a comparison parameter writes nothing.
  const {
    audience: askedAudience,
    volume: askedVolume,
    contractDuration: askedDuration,
    smartCharging: askedSmartCharging,
    terms: askedTerms,
  } = compareInputsFromUrl(searchParams);
  const askedForComparison = hasCompareFilterParam(searchParams);
  useEffect(() => {
    rememberCompareInputs(
      {
        audience: askedAudience,
        volume: askedVolume,
        contractDuration: askedDuration,
        smartCharging: askedSmartCharging,
        terms: askedTerms,
      },
      { urlHadFilterParam: askedForComparison }
    );
  }, [
    askedAudience,
    askedVolume,
    askedDuration,
    askedSmartCharging,
    askedTerms,
    askedForComparison,
  ]);

  const setParam = (key: string, val: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, val);
        return next;
      },
      { replace: true }
    );
  };

  const trackFilterChange = (filter: string, value: string | number) => {
    trackEvent('filter_change', {
      filter_name: filter,
      filter_value: String(value),
      surface: SURFACES.filters,
      language,
    });
  };

  const setAudience = (val: string) => {
    setParam('audience', val);
    trackFilterChange('audience', val);
  };
  // Volume changes from the slider fire per-pixel during drag; trackers are
  // attached at commit points (slider release, input blur, estimator apply).
  const setVolume = (val: number) => setParam('volume', val.toString());

  const audienceOptions: { value: string; i18nKey: string }[] = [
    { value: 'Particulier', i18nKey: 'particulier' },
    { value: 'Zakelijk', i18nKey: 'zakelijk' },
    { value: 'Alle', i18nKey: 'all' },
  ];

  // Same derived list as the results bar (src/lib/durationOptions.ts): only
  // durations a provider actually offers, plus whatever is selected right now
  // so an active chip is always deselectable.
  const contractOptions = useMemo(
    () => durationOptions(providers, contractDuration, t),
    [providers, contractDuration, t]
  );
  // A multi-select since 2026-10-16: a pick toggles one looptijd, by the same
  // rule as the results bar; a pick that changes nothing writes nothing.
  const setContractDuration = (option: string) => {
    const next = toggleDurationSelection(
      contractDuration,
      option,
      contractOptions.filter((o) => o.value !== ALL_DURATIONS).map((o) => o.value)
    );
    if (next === contractDuration) return;
    setParam('contractDuration', next);
    trackFilterChange('contract_duration', next);
  };

  return (
    // lg:p-7 is paid for by the hero's narrower column gap (64 → 48 px, one
    // extra 8 px per column), so the card's INNER width is unchanged at every
    // breakpoint: the audience toggle and the volume label row fit exactly as
    // they did before.
    <div className="bg-slate-800/95 border border-emerald-500/20 rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-7 shadow-2xl shadow-emerald-900/20 w-full relative overflow-hidden">
      {/* Decorative orb — desktop-only (md), like the hero's: a 256 px
          blur(64px) layer at 5 % effective opacity is invisible on a phone
          and expensive for its GPU to rasterise. */}
      <div className="hidden md:block absolute top-0 right-0 w-64 h-64 bg-emerald-900/10 rounded-full blur-3xl -z-10 opacity-50 translate-x-1/2 -translate-y-1/2" />

      {/* Step 1 — glass icon badge (kennisbank style) */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-emerald-900/40 p-2.5 rounded-xl shadow-sm shrink-0 border border-emerald-800/30">
          <Calculator className="w-5 h-5 text-emerald-500" />
        </div>
        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-400">
          {t('calculator.calculate_step')}
        </span>
      </div>

      {/* A returning visitor's last comparison, offered and never applied. It
          renders nothing until it has read the browser's storage after mount,
          so the baked card and the card that hydrates it are the same. */}
      <ResumeCompareChip />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
        {/* Audience toggle */}
        <div>
          <label className="block text-xs font-bold text-slate-400 mb-2">
            {t('filters.audience')}
          </label>
          <div className="flex bg-slate-900/60 p-1 rounded-xl border border-slate-700/50">
            {audienceOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAudience(opt.value)}
                className={cn(
                  'flex-1 py-2 text-[10px] sm:text-xs font-black rounded-lg transition-all focus-ring',
                  audience === opt.value
                    ? // Literal hexes: bg-white/text-emerald-800 are remapped tokens in
                      // light mode (white → near-black) and would invert the chip.
                      'bg-slate-700 text-emerald-300 shadow-sm [html.light_&]:bg-[#ffffff] [html.light_&]:text-[#065f46]'
                    : 'text-fg-muted hover:text-slate-300 hover:bg-slate-800/50'
                )}
              >
                {t(`filters.${opt.i18nKey}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Contract duration */}
        <div>
          <label id={durationLabelId} className="block text-xs font-bold text-slate-400 mb-2">
            {t('calculator.contract_duration')}
          </label>
          <div className="text-sm">
            <CustomDropdown
              labelledBy={durationLabelId}
              value={contractDuration}
              onChange={setContractDuration}
              options={contractOptions}
              multiple
              isSelected={(option) => isDurationSelected(contractDuration, option)}
              valueText={durationSelectionLabel(
                contractDuration,
                contractOptions,
                t('filters.all_durations'),
                (count) =>
                  fillTemplate(t('filters.durations_count'), {
                    count: formatInteger(count, language),
                  })
              )}
            />
          </div>
        </div>

        {/* Volume slider */}
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between mb-2 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <label
                htmlFor="filters-volume-input"
                className="block text-xs font-bold text-slate-400 truncate"
              >
                {t('filters.volume')}
              </label>
              <button
                type="button"
                onClick={() => {
                  setEstimatorOpen(true);
                  trackEvent('estimator_open', { surface: SURFACES.filters, language });
                }}
                // The modal's chunk starts loading before the click (lazyModals.ts).
                {...preloadOnIntent(loadEstimateKwhModal)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline shrink-0"
              >
                <Calculator className="w-3.5 h-3.5" aria-hidden="true" />
                {t('filters.volume_help')}
              </button>
            </div>
            <div className="flex items-center gap-1 bg-emerald-900/30 rounded-md px-2 py-0.5">
              <input
                id="filters-volume-input"
                type="text"
                inputMode="numeric"
                value={volumeInput}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setVolumeInput(raw);
                }}
                onBlur={() => {
                  // The field opens on the formatted volume ("2.500"), and
                  // parseInt of that text is 2: leaving the field without
                  // typing wrote volume=2 into the address — a comparison the
                  // card then remembered and offered back. Digits only, and an
                  // unchanged value writes nothing and records no change.
                  const parsed = Math.min(
                    Math.max(parseInt(volumeInput.replace(/[^0-9]/g, '')) || 0, VOLUME_MIN),
                    VOLUME_MANUAL_MAX
                  );
                  setVolumeInput(formatInteger(parsed, language));
                  if (parsed === volume) return;
                  setVolume(parsed);
                  trackFilterChange('volume', parsed);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                className="w-28 text-right text-xs font-black text-emerald-500 bg-transparent outline-none border-b border-emerald-500/30 focus:border-emerald-500"
              />
              <span className="text-xs font-black text-emerald-500">{t('table.kwh')}</span>
            </div>
          </div>
          <div className="space-y-1 relative pt-2">
            <input
              type="range"
              aria-label={t('filters.volume_aria')}
              min={VOLUME_MIN}
              max={VOLUME_MAX}
              step={VOLUME_STEP}
              value={Math.min(VOLUME_MAX, volume)}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setVolume(val);
                setVolumeInput(formatInteger(val, language));
              }}
              onMouseUp={(e) =>
                trackFilterChange('volume', parseInt((e.target as HTMLInputElement).value))
              }
              onTouchEnd={(e) =>
                trackFilterChange('volume', parseInt((e.target as HTMLInputElement).value))
              }
              onKeyUp={(e) =>
                trackFilterChange('volume', parseInt((e.target as HTMLInputElement).value))
              }
              className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-400 transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-700/50">
        <button
          type="button"
          onClick={() => {
            trackEvent('show_results_click', {
              audience,
              volume,
              contract_duration: contractDuration,
              surface: SURFACES.filters,
              language,
            });
            scrollToCompare();
          }}
          className="w-full px-8 py-4 bg-emerald-700 hover:bg-emerald-800 text-white text-base font-black rounded-2xl transition-all shadow-xl shadow-emerald-900/30 hover:shadow-2xl hover:shadow-emerald-900/40 flex items-center justify-center gap-3 group"
        >
          <Zap className="w-5 h-5" />
          {t('filters.show_results')}
          <ArrowDown className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
        </button>
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setMidCheckerOpen(true);
              trackEvent('mid_checker_open', { surface: SURFACES.filters, language });
            }}
            {...preloadOnIntent(loadMidCheckerModal)}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline"
          >
            <Gauge className="w-3.5 h-3.5" aria-hidden="true" />
            {t('mid_checker.trigger_label')}
          </button>
        </div>
      </div>

      <LazyOnOpen open={estimatorOpen}>
        <EstimateKwhModal
          open={estimatorOpen}
          onClose={() => setEstimatorOpen(false)}
          audience={audience}
          onApply={(kwh) => {
            setVolume(kwh);
            setVolumeInput(formatInteger(kwh, language));
            setEstimatorOpen(false);
            // The apply event (estimator_apply / estimator_fleet_apply, disjoint)
            // fires inside the modal, which knows which flow was used. Here only
            // the filter-state signal fires — same as a hand-typed volume change.
            trackFilterChange('volume', kwh);
          }}
        />
      </LazyOnOpen>
      <LazyOnOpen open={midCheckerOpen}>
        <MidCheckerModal open={midCheckerOpen} onClose={() => setMidCheckerOpen(false)} />
      </LazyOnOpen>
    </div>
  );
};
